
import React, { useMemo, useState } from 'react';
// import useTranslate from 'src/utils/useTranslate';
import { axiosInstance } from 'src/utils/sendApi';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from 'src/store/configureStore';
import { setDefaultHeaders } from 'src/store/actions/defaultHeaders';
import { antdTheme, currentPlatform } from 'src/constants';
import { loginFailure, loginNotVerified, loginSuccess, setOtp, setVerification, updateLoginResponse } from 'src/store/actions/login';
import { trackEvent } from 'src/analytics';
import { handleFirebaseLogin } from 'src/utils/firebase';
import { languageToggle } from 'src/store/actions/language';
import { useHistory } from 'react-router-dom';
import { messages } from 'src/utils/hooks';
import { getIpDetails } from 'src/services/login';
import { StyledButton } from 'src/styled_components/StyledButton';
import { Space } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import useTranslate from 'src/utils/useTranslate';

export default function LoginPasskey({ user_email }: { user_email: string }) {
    const [t] = useTranslate()
    const history = useHistory()
    const dispatch = useDispatch()
    const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
    const defaultHeaders = useSelector((state: StoreState) => state.defaultHeaders);
    const currentLanguage = useSelector((state: StoreState) => state.language.current);

    const SWB = (window as any).SimpleWebAuthnBrowser;

    const headers = {
        ...defaultHeaders,
    };

    const isValidEmail = useMemo(() => {
        const regex = new RegExp(
            /^([a-zA-Z0-9_\-.]+)@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.)|(([a-zA-Z0-9-]+\.)+))([a-zA-Z]{2,4}|[0-9]{1,3})(\]?)$/
        );
        const isValidEmail = regex.test(user_email?.trim())
        return isValidEmail
    }, [user_email])

    const handleLoginViaPassKey = async () => {
        const SWB = (window as any).SimpleWebAuthnBrowser;
        if (!SWB.browserSupportsWebAuthn()) {
            console.log('WebAuthn not supported');
            return
        }
        setLoadingStatus(true);

        try {
            const { data: options } = await axiosInstance.post(process.env.REACT_APP_API_URL + 'v1/data/_passkey_login_begin', { user_email }, { headers: headers });
            const credential = await SWB.startAuthentication({ optionsJSON: options });
            const response = await axiosInstance.post<any>(process.env.REACT_APP_API_URL + 'v1/data/_passkey_login_finish', { user_email, credential }, { headers: headers });

            const result = response?.data?.result

            handleLogin(result)
        } catch (err: any) {
            if (err instanceof SWB?.WebAuthnError && err?.name === 'NotAllowedError') {
                console.log('User canceled authentication');
                return;
            }
            setLoadingStatus(false)
            const errorMessage = err?.response?.data?.detail || err?.response?.data?.message || messages[currentLanguage].internal_server_error;
            dispatch(loginFailure(errorMessage));
        } finally {
            setLoadingStatus(false)
        }
    };


    const handleLogin = async (result: any) => {
        const platform = currentPlatform
        let ln = currentLanguage

        try {
            const { data, message, status, redirect } = result
            const userId = data?.user_id
            if (status === "Success") {

                const {
                    data: ipData
                } = await getIpDetails()
                const ip = (ipData as any)?.ip
                ln = data?.language
                dispatch(
                    setDefaultHeaders({
                        ...defaultHeaders,
                        platform,
                        lang: ln,
                    })
                );
                if (!['en', 'ja'].includes(data?.language)) localStorage.setItem("isLanguageAnnouncement", "true")
                if (!!data?.language) {
                    dispatch(languageToggle(data.language))
                }
                if (data.user_verified && !data.user_access && platform === "connect") {
                    history.push("/access-denied", {
                        user_email: data.email,
                        user_name: data.user_name,
                        user_verified: data.user_verified,
                        user_access: data.user_access,
                    });
                }
                if (!data?.user_access) {
                    dispatch(loginNotVerified());
                    history.push("/access-denied", {
                        user_email: data?.email,
                        user_name: data?.user_name,
                        user_verified: data?.user_verified,
                        user_access: data?.user_access,
                    });
                }
                else if (data?.otp) {
                    dispatch(setOtp(true));
                    dispatch(
                        updateLoginResponse({ user_id: data?.user_id, partial: true, user_email: user_email, user_loggedin_IP: ip })
                    );
                } else if (data?.uri) {
                    dispatch(
                        setVerification({
                            otpVerificationRequired: true,
                            requestOtpData: data.uri,
                        })
                    );
                    dispatch(
                        updateLoginResponse({ user_id: data?.user_id, partial: true, user_email: data?.email ?? user_email, user_loggedin_IP: ip })
                    );
                } else if (data.user_access) {
                    trackEvent(userId, "User logged in successfully", {
                        "Login details": { data },
                    });
                    await handleFirebaseLogin(data?.firebase_auth_token)
                    dispatch(loginSuccess(data));
                }
                setLoadingStatus(false)
            } else {
                setLoadingStatus(false)

                trackEvent(userId, "User failed to log in");
                if (redirect) {
                    dispatch(
                        loginFailure(
                            `${message}. <br> <a style="color:white;font-weight: bold" href=${data.url} rel="noreferrer" target="_blank">Click Here to Login to ${data.platform}</a>`
                        )
                    );
                }
            }
        } catch (err: any) {
            console.log({ err })
            if (err instanceof SWB?.WebAuthnError && err?.name === 'NotAllowedError') {
                console.log('User canceled authentication');
                return;
            }
            setLoadingStatus(false)
            dispatch(loginFailure(messages[ln].internal_server_error))
        }
    }

    if (!SWB?.browserSupportsWebAuthn()) {
        return null
    }


    return (
        <>
            <StyledButton
                size="large"
                className="round-text social-btn"
                loading={loadingStatus}
                disabled={!Boolean(user_email) || !isValidEmail}
                onClick={handleLoginViaPassKey}
                style={{
                    whiteSpace: "normal",
                    display: "block",
                    height: "max-content",
                    textAlign: "center",
                    padding: "8px, 16px, 8px, 16px",
                    width: "100%",
                }}
            >
                <Space style={{ fontWeight: 600, padding: "5px" }}>
                    <KeyOutlined style={{ fontSize: antdTheme.fontSizeHeading2 }} />
                    {t("common.loginWithPasskey")}
                </Space>
            </StyledButton>
        </>
    );
}
